import { ReactNode } from 'react';
import { CoachStatus, CoachSubStatus, CoachStatusTree, CoachSubStatusLabelMap } from '@airreading/common/data';
import { UseFormReturn } from 'react-hook-form';
import { allCoachStatuses, getCoachStatusLabel } from '@/lib/coach-utils.js';
import { EditTeachingFormSchema } from './teaching-section-card.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuCheckboxItem,
  DropdownMenuPortal,
} from '@/components/ui/dropdown-menu.js';
import { Button } from '@/components/ui/button.js';
import { FormControl, FormField, FormItem, FormLabel } from '@/components/ui/form.js';
import { cn } from '@/lib/tailwind-utils.js';
import { ChevronDown } from 'lucide-react';

interface CoachStatusMenuProps {
  form: UseFormReturn<EditTeachingFormSchema>;
  editing: boolean;
  label: ReactNode;
  disabled?: boolean;
  className?: string;
}

type SubStatusesByStatus = Partial<Record<CoachStatus, CoachSubStatus[]>>;

export function CoachStatusMenu({ form, editing, label, disabled = false, className = '' }: CoachStatusMenuProps) {
  const selectedStatus = form.watch('status') as CoachStatus;
  const subStatusesByStatus: SubStatusesByStatus = form.watch('subStatusesByStatus') ?? {};
  const isMenuDisabled = disabled || !editing;

  const getSelectedByStatus = (status: CoachStatus) => {
    return subStatusesByStatus[status] ?? [];
  };

  const setSelectedForStatus = (status: CoachStatus, next: CoachSubStatus[]) => {
    form.setValue(
      'subStatusesByStatus',
      {
        ...subStatusesByStatus,
        [status]: next,
      },
      { shouldDirty: true, shouldTouch: true },
    );
  };

  const handleStatusChange = (status: CoachStatus) => {
    form.setValue('status', status, { shouldDirty: true, shouldTouch: true });

    const hasChildren = (CoachStatusTree[status] ?? []).length > 0;

    if (!hasChildren) {
      setSelectedForStatus(status, []);
    }
  };

  const handleSubStatusToggle = (subStatus: CoachSubStatus, parentStatus: CoachStatus) => {
    if (selectedStatus !== parentStatus) {
      form.setValue('status', parentStatus, { shouldDirty: true, shouldTouch: true });
    }

    const current = getSelectedByStatus(parentStatus);
    const next = current.includes(subStatus) ? current.filter((ss) => ss !== subStatus) : [...current, subStatus];

    setSelectedForStatus(parentStatus, next);
  };

  const checkboxLikeClass = cn(
    '[&>span]:h-4 [&>span]:w-4',
    '[&>span]:rounded-sm [&>span]:aspect-square',
    '[&>span]:border [&>span]:border-gray-400',
    '[&>span>span>svg]:rounded-sm',
    '[&>span>span>svg]:bg-primary [&>span>span>svg]:text-primary-foreground',
    '[&>span>span>svg]:stroke-[2]',
    '[&>span]:data-[state=checked]:border-none',
  );

  return (
    <FormField
      control={form.control}
      name="status"
      render={() => (
        <FormItem className={className}>
          <FormLabel className="font-bold inline-block w-full">{label}</FormLabel>
          <FormControl>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-2/3 justify-between" disabled={isMenuDisabled}>
                  <span>{getCoachStatusLabel(selectedStatus) || 'Select status'}</span>
                  <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuPortal>
                <DropdownMenuContent align="start" className="w-[--radix-dropdown-menu-trigger-width]">
                  {allCoachStatuses.map((status) => {
                    const subStatuses = CoachStatusTree[status] || [];
                    const hasSubMenu = subStatuses.length > 0;

                    if (hasSubMenu) {
                      const selectedForThisStatus = getSelectedByStatus(status);

                      return (
                        <DropdownMenuSub key={status}>
                          <DropdownMenuSubTrigger
                            className={cn(
                              selectedStatus === status ? 'bg-accent' : '',
                              isMenuDisabled ? 'opacity-50' : '',
                            )}
                            onPointerDown={(e) => {
                              e.preventDefault();
                              if (editing) {
                                handleStatusChange(status);
                              }
                            }}
                          >
                            {getCoachStatusLabel(status)}
                          </DropdownMenuSubTrigger>
                          <DropdownMenuPortal>
                            <DropdownMenuSubContent
                              className="w-full mr-2 max-h-80 overflow-auto"
                              sideOffset={2}
                              alignOffset={-5}
                            >
                              {subStatuses.map((subStatus) => (
                                <DropdownMenuCheckboxItem
                                  key={`${status}-${subStatus}`}
                                  checked={selectedForThisStatus.includes(subStatus)}
                                  onCheckedChange={() => handleSubStatusToggle(subStatus, status)}
                                  onSelect={(e) => e.preventDefault()}
                                  disabled={isMenuDisabled}
                                  className={checkboxLikeClass}
                                >
                                  <div className="ml-1">{CoachSubStatusLabelMap[subStatus]}</div>
                                </DropdownMenuCheckboxItem>
                              ))}
                            </DropdownMenuSubContent>
                          </DropdownMenuPortal>
                        </DropdownMenuSub>
                      );
                    }

                    return (
                      <DropdownMenuItem
                        key={status}
                        onClick={() => handleStatusChange(status)}
                        className={selectedStatus === status ? 'bg-accent' : ''}
                        disabled={isMenuDisabled}
                      >
                        {getCoachStatusLabel(status)}
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenuPortal>
            </DropdownMenu>
          </FormControl>
        </FormItem>
      )}
    />
  );
}
